import type {
	QuestionnaireItem,
	QuestionnaireItemEnableWhen,
	QuestionnaireResponse,
	QuestionnaireResponseItem,
	QuestionnaireResponseItemAnswer,
} from "fhir/r4";

import { codingsMatch } from "./coding";
import { ENABLE_WHEN_EXPRESSION_URL } from "../extensions";
import { evaluateFhirPathBoolean } from "../fhirpath";

/**
 * Returns `true` when the question should be considered answerable for the given response, taking `enableWhen` (with
 * `enableBehavior`) and the SDC `enableWhenExpression` extension into account. Items without any gating always count as
 * enabled.
 */
export const isEnabled = (item: QuestionnaireItem, response: QuestionnaireResponse): boolean => {
	const expression = item.extension?.find((extension) => extension.url === ENABLE_WHEN_EXPRESSION_URL)?.valueExpression
		?.expression;
	if (expression && !evaluateFhirPathBoolean(response, expression)) {
		return false;
	}

	const conditions = item.enableWhen;
	if (!conditions?.length) {
		return true;
	}

	const behavior = item.enableBehavior ?? "all";
	if (behavior === "any") {
		return conditions.some((condition) => evaluateEnableWhen(condition, response));
	}
	return conditions.every((condition) => evaluateEnableWhen(condition, response));
};

const ENABLE_WHEN_ANSWER_FIELDS = [
	"answerBoolean",
	"answerDecimal",
	"answerInteger",
	"answerDate",
	"answerDateTime",
	"answerTime",
	"answerString",
	"answerCoding",
	"answerQuantity",
	"answerReference",
] as const;

type EnableWhenAnswerField = (typeof ENABLE_WHEN_ANSWER_FIELDS)[number];

/**
 * For each `enableWhen.answer[x]` field FHIR R4 defines, the matching `value[x]` field on
 * `QuestionnaireResponse.item.answer`. Mapping is 1:1.
 *
 * @see https://hl7.org/fhir/R4/questionnaire-definitions.html#Questionnaire.item.enableWhen.answer_x_
 */
const ENABLE_WHEN_TO_VALUE_FIELD = {
	answerBoolean: "valueBoolean",
	answerDecimal: "valueDecimal",
	answerInteger: "valueInteger",
	answerDate: "valueDate",
	answerDateTime: "valueDateTime",
	answerTime: "valueTime",
	answerString: "valueString",
	answerCoding: "valueCoding",
	answerQuantity: "valueQuantity",
	answerReference: "valueReference",
} as const satisfies Record<EnableWhenAnswerField, keyof QuestionnaireResponseItemAnswer>;

const findEnableWhenAnswerField = (condition: QuestionnaireItemEnableWhen): EnableWhenAnswerField | undefined =>
	ENABLE_WHEN_ANSWER_FIELDS.find((field) => condition[field] !== undefined);

const evaluateEnableWhen = (condition: QuestionnaireItemEnableWhen, response: QuestionnaireResponse): boolean => {
	const answers = collectAnswersByLinkId(response.item ?? [], condition.question);

	if (condition.operator === "exists") {
		const expected = condition.answerBoolean ?? true;
		return answers.length > 0 === expected;
	}

	const answerField = findEnableWhenAnswerField(condition);
	if (!answerField) {
		return false;
	}
	const expected = condition[answerField];
	const valueField = ENABLE_WHEN_TO_VALUE_FIELD[answerField];

	return answers.some((answer) => compareValues(answer, valueField, expected, condition.operator));
};

const compareValues = (
	answer: QuestionnaireResponseItemAnswer,
	valueField: keyof QuestionnaireResponseItemAnswer,
	expected: unknown,
	operator: QuestionnaireItemEnableWhen["operator"],
): boolean => {
	const actual = answer[valueField];
	if (actual === undefined) return false;

	if (valueField === "valueCoding") {
		const equal = codingsMatch(expected, actual);
		return operator === "=" ? equal : operator === "!=" ? !equal : false;
	}

	switch (operator) {
		case "=":
			return actual === expected;
		case "!=":
			return actual !== expected;
		case ">":
		case "<":
		case ">=":
		case "<=":
			return compareOrderedValues(actual, expected, operator);
		default:
			return false;
	}
};

const compareOrderedValues = (actual: unknown, expected: unknown, operator: ">" | "<" | ">=" | "<="): boolean => {
	if (typeof actual === "number" && typeof expected === "number") {
		return compareOrderedValue(actual, expected, operator);
	}
	if (typeof actual === "string" && typeof expected === "string") {
		// ISO 8601 dates / times are lexicographically ordered at matching precision.
		return compareOrderedValue(actual, expected, operator);
	}
	return false;
};

const compareOrderedValue = <T extends number | string>(
	actual: T,
	expected: T,
	operator: ">" | "<" | ">=" | "<=",
): boolean => {
	switch (operator) {
		case ">":
			return actual > expected;
		case "<":
			return actual < expected;
		case ">=":
			return actual >= expected;
		case "<=":
			return actual <= expected;
	}
};

const collectAnswersByLinkId = (
	items: QuestionnaireResponseItem[],
	linkId: string,
): QuestionnaireResponseItemAnswer[] => {
	const collectedAnswers: QuestionnaireResponseItemAnswer[] = [];
	const stack: QuestionnaireResponseItem[] = [...items];
	let node: QuestionnaireResponseItem | undefined;
	while ((node = stack.pop()) !== undefined) {
		if (node.linkId === linkId && node.answer) {
			collectedAnswers.push(...node.answer);
		}
		if (node.item) stack.push(...node.item);
		if (node.answer) {
			for (const answer of node.answer) {
				if (answer.item) stack.push(...answer.item);
			}
		}
	}
	return collectedAnswers;
};
